'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { CustomizationSelection } from '@/store/cart/cart-store';
import type { Customization, MenuItem } from '@/types/item';

interface CustomizationFormValues {
  customizations: Record<string | number, string | string[]>;
  quantity: number;
  note?: string;
}

interface UseCustomizationFormProps {
  item: MenuItem | null;
  customizations: Customization[];
}

export function useCustomizationForm({
  item,
  customizations,
}: UseCustomizationFormProps) {
  const form = useForm<CustomizationFormValues>({
    defaultValues: {
      customizations: {},
      quantity: 1,
      note: '',
    },
  });

  const { setValue, reset, handleSubmit, setError, clearErrors } = form;

  const formQuantity = useWatch({ control: form.control, name: 'quantity' });
  const formCustomizations = useWatch({
    control: form.control,
    name: 'customizations',
  });

  // Track initialization to prevent unnecessary resets
  const initializationRef = useRef({
    itemId: null as string | null,
    cartItemId: undefined as string | undefined,
    customizationHash: '',
    initialized: false,
  });

  // Helper function to create customization hash for comparison
  const createCustomizationHash = useCallback(
    (existingCustomizations: any[]) => {
      if (!existingCustomizations?.length) return '';

      // Handle both formats
      const isStructuredFormat = existingCustomizations.some(
        (item) => item._id && item.selectedOption
      );

      if (isStructuredFormat) {
        return JSON.stringify(
          existingCustomizations
            .map((c: any) => `${c._id}:${c.selectedOption?._id || 'none'}`)
            .sort()
        );
      } else {
        return JSON.stringify(
          existingCustomizations
            .map((c: any) => `${c.id}:${c.quantity || 1}`)
            .sort()
        );
      }
    },
    []
  );

  // Helper function to map existing customizations to form values
  const mapExistingCustomizations = useCallback(
    (existingCustomizations: any[]) => {
      const defaults: Record<string | number, string | string[]> = {};

      // Handle both flat format [{id, quantity}] and structured format [{_id, name, selectedOption}]
      const isStructuredFormat = existingCustomizations.some(
        (item) => item._id && item.selectedOption
      );

      // Group customizations by their parent customization group
      customizations.forEach((customizationGroup) => {
        const isMultiSelect = customizationGroup.max_permitted > 1;
        let selectedOptions: string[] = [];

        if (isStructuredFormat) {
          // Handle structured format
          selectedOptions = existingCustomizations
            .filter((item) => item._id === customizationGroup._id)
            .map((item) => item.selectedOption?._id)
            .filter((id): id is string => Boolean(id));
        } else {
          // Handle flat format
          selectedOptions = existingCustomizations
            .filter((item) => {
              // Check if this customization item belongs to current group
              return customizationGroup.options?.some(
                (option) => option._id === item.id
              );
            })
            .map((item) => item.id)
            .filter((id): id is string => Boolean(id));
        }

        if (selectedOptions.length > 0) {
          if (isMultiSelect) {
            // For multi-select, store as array
            defaults[customizationGroup._id] = selectedOptions;
          } else {
            // For single-select, store first option only
            defaults[customizationGroup._id] = selectedOptions[0] || '';
          }
        } else {
          // Set default empty value based on type
          defaults[customizationGroup._id] = isMultiSelect ? [] : '';
        }
      });

      return defaults;
    },
    [customizations]
  );

  // Helper function to set default options for new items
  const setDefaultOptions = useCallback(() => {
    const defaults: Record<string | number, string | string[]> = {};

    customizations.forEach((customization) => {
      const isMultiSelect = customization.max_permitted > 1;
      const defaultOption = customization.options?.find(
        (option) => option.price === 0
      );

      if (defaultOption && customization.min_permitted > 0) {
        defaults[customization._id] = isMultiSelect
          ? [String(defaultOption._id)]
          : String(defaultOption._id);
      } else {
        // Set appropriate empty value
        defaults[customization._id] = isMultiSelect ? [] : '';
      }
    });

    return defaults;
  }, [customizations]);

  // Initialize form when item or customizations change
  useEffect(() => {
    if (!item || !customizations.length) return;

    const existingCustomizations = (item as any).customizations;
    const cartItemId = (item as any).cartItemId;
    const currentItemId = String(item._id);
    const customizationHash = createCustomizationHash(
      existingCustomizations || []
    );

    const current = initializationRef.current;
    const shouldReset =
      current.itemId !== currentItemId ||
      current.cartItemId !== cartItemId ||
      current.customizationHash !== customizationHash ||
      !current.initialized;

    if (shouldReset) {
      const formDefaults =
        existingCustomizations?.length > 0
          ? mapExistingCustomizations(existingCustomizations)
          : setDefaultOptions();

      reset({
        customizations: formDefaults,
        quantity: (item as any).cartItemId
          ? (item as any).cartQuantity || 1
          : 1,
        note: (item as any).note || '',
      });

      // Update tracking reference
      initializationRef.current = {
        itemId: currentItemId,
        cartItemId,
        customizationHash,
        initialized: true,
      };
    }
  }, [
    item,
    customizations,
    reset,
    createCustomizationHash,
    mapExistingCustomizations,
    setDefaultOptions,
  ]);

  // Get selected customizations in the required format
  const getSelectedCustomizations =
    useCallback((): CustomizationSelection[] => {
      if (!customizations.length) return [];

      return customizations.flatMap((customization: Customization) => {
        const selected = formCustomizations[customization._id];
        if (!selected) return [];

        if (Array.isArray(selected)) {
          // Multi-select: return array of selections
          const validSelections: CustomizationSelection[] = [];
          selected.forEach((optionId) => {
            if (typeof optionId !== 'string') return;
            const option = customization.options?.find(
              (o) => o._id === optionId
            );
            if (!option) return;
            validSelections.push({
              _id: customization._id,
              name: customization.name,
              selectedOption: {
                _id: String(option._id),
                name: option.name,
                price: option.price,
              },
            });
          });
          return validSelections;
        } else if (selected && typeof selected === 'string') {
          // Single-select: return single selection
          const option = customization.options?.find((o) => o._id === selected);
          if (!option) return [];
          return [
            {
              _id: customization._id,
              name: customization.name,
              selectedOption: {
                _id: String(option._id),
                name: option.name,
                price: option.price,
              },
            },
          ];
        }
        return [];
      });
    }, [customizations, formCustomizations]);

  // Validate customizations
  const validateCustomizations = useCallback(() => {
    let isValid = true;

    customizations.forEach((customization) => {
      const selected = formCustomizations[customization._id];
      let selectedCount = 0;

      if (Array.isArray(selected)) {
        selectedCount = selected.filter(Boolean).length;
      } else if (selected && typeof selected === 'string') {
        selectedCount = 1;
      }

      const fieldName = `customizations.${customization._id}` as any;

      if (
        customization.min_permitted > 0 &&
        selectedCount < customization.min_permitted
      ) {
        setError(fieldName, {
          type: 'manual',
          message: `Vui lòng chọn ít nhất ${customization.min_permitted} tuỳ chọn`,
        });
        isValid = false;
      } else if (
        customization.max_permitted > 0 &&
        selectedCount > customization.max_permitted
      ) {
        setError(fieldName, {
          type: 'manual',
          message: `Chỉ được chọn tối đa ${customization.max_permitted} tuỳ chọn`,
        });
        isValid = false;
      } else {
        clearErrors(fieldName);
      }
    });

    return isValid;
  }, [customizations, formCustomizations, setError, clearErrors]);

  // Calculate total price
  const calculateTotalPrice = useCallback((): number => {
    if (!item) return 0;

    const basePrice = (item.discounted_price ?? item.price) * formQuantity;
    const customizationsPrice =
      getSelectedCustomizations().reduce(
        (sum, customization) =>
          sum + (customization.selectedOption?.price ?? 0),
        0
      ) * formQuantity;

    return basePrice + customizationsPrice;
  }, [item, formQuantity, getSelectedCustomizations]);

  return {
    form,
    formQuantity,
    formCustomizations,
    setValue,
    handleSubmit,
    getSelectedCustomizations,
    validateCustomizations,
    calculateTotalPrice,
  };
}
